import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from './firebase'
import { OrgChartService, type TeamMember } from './orgChartService'
import type { ActiveSession, TimesheetDayDoc, TimesheetScreen, WorkLocation } from '../types/timesheet'

export interface LiveWorkStatus {
  /** Team Brevity member id. */
  memberId: string
  firebaseUid: string | null
  email: string
  name: string
  avatar: string | null
  screen: TimesheetScreen | null
  location: WorkLocation | null
  currentTask: string | null
  activeKind: ActiveSession['kind'] | null
  /**
   * Tape art for the live task. Prefer the Cloud Function binding
   * (`tapeBackground/current`) over a URL copied onto the timesheet.
   */
  tapeBackgroundUrl: string | null
  sickLeaveToday: boolean
  /** User has locked out for this Auckland day (survives thanks → open). */
  lockedOutToday: boolean
  hasTimesheet: boolean
  updatedAt: string | null
}

const SCREENS = new Set<TimesheetScreen>([
  'open',
  'locking-in',
  'locked-in',
  'distraction',
  'break',
  'lock-out',
  'lock-out-planning',
  'thanks',
])

/** Org-wide day key (Pacific/Auckland), matching calendar cache conventions. */
export function aucklandDayKey(date: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Pacific/Auckland',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
}

export function formatWorkLocation(location: WorkLocation | null | undefined): string {
  if (!location) return '—'
  if (location === 'home') return 'Home'
  if (location === 'office') return 'Office'
  const trimmed = location.trim()
  return trimmed || '—'
}

export function currentTaskLabel(active: ActiveSession | null | undefined): string | null {
  if (!active) return null
  if (active.kind === 'break') return active.forcedBreak ? 'Forced break' : 'On a break'
  if (active.kind === 'distraction') {
    return active.description?.trim() || 'Distraction'
  }
  const fromDesc = active.description?.trim()
  if (fromDesc) return fromDesc
  const fromPwf = active.pwfTaskName?.trim()
  return fromPwf || null
}

function tapeTaskKey(name: string | null | undefined): string {
  return (name || '').trim().toLowerCase().slice(0, 160)
}

function sameTapeTask(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = tapeTaskKey(a)
  const right = tapeTaskKey(b)
  return Boolean(left) && left === right
}

/** Task the deck art belongs to — live task, or the stashed task while paused. */
function artTaskName(active: ActiveSession | null | undefined): string | null {
  if (!active) return null
  if (active.kind === 'task') return active.description?.trim() || null
  return active.resumeTask?.description?.trim() || null
}

/** Prefer live task art; while on break/distraction, reuse stashed resumeTask art. */
export function resolveTapeBackgroundUrl(active: ActiveSession | null | undefined): string | null {
  if (!active) return null
  const fromActive =
    typeof active.tapeBackgroundUrl === 'string' ? active.tapeBackgroundUrl.trim() : ''
  if (fromActive) return fromActive
  const fromResume =
    typeof active.resumeTask?.tapeBackgroundUrl === 'string'
      ? active.resumeTask.tapeBackgroundUrl.trim()
      : ''
  return fromResume || null
}

function parseTimesheetDayDoc(raw: unknown): TimesheetDayDoc | null {
  if (!raw || typeof raw !== 'object') return null
  const row = raw as Partial<TimesheetDayDoc> & {
    blocks?: Array<{ pushedToPwf?: boolean }>
  }
  if (typeof row.dayKey !== 'string') return null
  if (typeof row.screen !== 'string' || !SCREENS.has(row.screen as TimesheetScreen)) return null
  if (typeof row.location !== 'string') return null

  let active: ActiveSession | null = null
  if (row.active && typeof row.active === 'object') {
    const a = row.active as ActiveSession
    if (
      (a.kind === 'task' || a.kind === 'distraction' || a.kind === 'break') &&
      typeof a.blockId === 'string' &&
      typeof a.description === 'string'
    ) {
      active = a
    }
  }

  const onLockOutFlow =
    row.screen === 'lock-out' ||
    row.screen === 'lock-out-planning' ||
    row.screen === 'thanks'
  const pushedAny =
    Array.isArray(row.blocks) && row.blocks.some((b) => b?.pushedToPwf === true)

  return {
    dayKey: row.dayKey,
    screen: row.screen as TimesheetScreen,
    location: row.location as WorkLocation,
    active,
    sickLeaveToday: row.sickLeaveToday === true,
    lockedOutToday: row.lockedOutToday === true || onLockOutFlow || pushedAny,
    updatedAt: typeof row.updatedAt === 'string' ? row.updatedAt : undefined,
    writeId: typeof row.writeId === 'string' ? row.writeId : undefined,
  }
}

function avatarFor(member: TeamMember): string | null {
  const url = member.thumbnailPhotoUrl?.trim()
  return url || null
}

function baseStatus(member: TeamMember, firebaseUid: string | null): LiveWorkStatus {
  return {
    memberId: member.id,
    firebaseUid,
    email: member.primaryEmail,
    name: member.name.fullName,
    avatar: avatarFor(member),
    screen: null,
    location: null,
    currentTask: null,
    activeKind: null,
    tapeBackgroundUrl: null,
    sickLeaveToday: false,
    lockedOutToday: false,
    hasTimesheet: false,
    updatedAt: null,
  }
}

function applyTimesheet(status: LiveWorkStatus, docData: TimesheetDayDoc | null): LiveWorkStatus {
  if (!docData) {
    return {
      ...status,
      screen: null,
      location: null,
      currentTask: null,
      activeKind: null,
      tapeBackgroundUrl: null,
      sickLeaveToday: false,
      lockedOutToday: false,
      hasTimesheet: false,
      updatedAt: null,
    }
  }

  return {
    ...status,
    screen: docData.screen,
    location: docData.location,
    currentTask: currentTaskLabel(docData.active),
    activeKind: docData.active?.kind ?? null,
    tapeBackgroundUrl: resolveTapeBackgroundUrl(docData.active),
    sickLeaveToday: docData.sickLeaveToday === true,
    lockedOutToday: docData.lockedOutToday === true,
    hasTimesheet: true,
    updatedAt: docData.updatedAt ?? null,
  }
}

export class LiveWorkService {
  private static instance: LiveWorkService

  static getInstance(): LiveWorkService {
    if (!LiveWorkService.instance) {
      LiveWorkService.instance = new LiveWorkService()
    }
    return LiveWorkService.instance
  }

  /**
   * Visible Team Brevity directory members (excludes hidden / inactive).
   * Sorted by full name for a stable board order.
   */
  async getVisibleMembers(): Promise<TeamMember[]> {
    const orgChartService = OrgChartService.getInstance()
    const members = await orgChartService.getAllTeamMembers()
    return members
      .filter((m) => !m.hidden)
      .sort((a, b) => a.name.fullName.localeCompare(b.name.fullName, 'en', { sensitivity: 'base' }))
  }

  /** Map lowercase email → Firebase Auth uid from `users` collection. */
  async getEmailToUidMap(): Promise<Map<string, string>> {
    const snap = await getDocs(collection(db, 'users'))
    const map = new Map<string, string>()
    snap.docs.forEach((d) => {
      const email = (d.data().email as string | undefined)?.toLowerCase()
      if (email) map.set(email, d.id)
    })
    return map
  }

  /**
   * Subscribe to today's timesheet for each visible member (joined by email → uid).
   * Calls `onUpdate` whenever any member's status changes.
   */
  subscribeTeamLiveWork(
    members: TeamMember[],
    emailToUid: Map<string, string>,
    dayKey: string,
    onUpdate: (statuses: LiveWorkStatus[]) => void
  ): Unsubscribe {
    const byMemberId = new Map<string, LiveWorkStatus>()
    const unsubs: Unsubscribe[] = []

    const artTaskByMember = new Map<string, string | null>()
    const bindingByUid = new Map<string, { taskName: string; imageUrl: string } | null>()

    const emitResolved = () => {
      onUpdate(
        members.map((m) => {
          const status = byMemberId.get(m.id)!
          const task = artTaskByMember.get(m.id) ?? null
          const binding = status.firebaseUid ? bindingByUid.get(status.firebaseUid) : null
          const fromBinding =
            binding && task && sameTapeTask(binding.taskName, task) ? binding.imageUrl : null
          return {
            ...status,
            tapeBackgroundUrl: fromBinding || status.tapeBackgroundUrl,
          }
        })
      )
    }

    members.forEach((member) => {
      const uid = emailToUid.get(member.primaryEmail.toLowerCase()) ?? null
      byMemberId.set(member.id, baseStatus(member, uid))
      artTaskByMember.set(member.id, null)

      if (!uid) return

      const ref = doc(db, 'users', uid, 'timesheets', dayKey)
      unsubs.push(
        onSnapshot(
          ref,
          (snap) => {
            const parsed = snap.exists() ? parseTimesheetDayDoc(snap.data()) : null
            const prev = byMemberId.get(member.id)!
            artTaskByMember.set(member.id, artTaskName(parsed?.active))
            byMemberId.set(member.id, applyTimesheet({ ...prev, firebaseUid: uid }, parsed))
            emitResolved()
          },
          (err) => {
            console.error(`Live work listen failed for ${member.primaryEmail}:`, err)
          }
        )
      )

      const bindingRef = doc(db, 'users', uid, 'tapeBackground', 'current')
      unsubs.push(
        onSnapshot(
          bindingRef,
          (snap) => {
            if (!snap.exists()) {
              bindingByUid.set(uid, null)
            } else {
              const row = snap.data() as { taskName?: string; imageUrl?: string }
              const taskName = typeof row.taskName === 'string' ? row.taskName.trim() : ''
              const imageUrl = typeof row.imageUrl === 'string' ? row.imageUrl.trim() : ''
              bindingByUid.set(uid, taskName && imageUrl ? { taskName, imageUrl } : null)
            }
            emitResolved()
          },
          (err) => {
            console.error(`Tape binding listen failed for ${member.primaryEmail}:`, err)
          }
        )
      )
    })

    emitResolved()

    return () => {
      unsubs.forEach((u) => u())
    }
  }
}
